// @flow

import React, { type Node, useCallback, useMemo } from 'react';
import {
  normalizeArgentineDocument,
  type ArgentineDocumentType,
} from '@tectonics/prs/argentina';
import { formatValueWithMask } from '@tectonics/components/input-mask';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useArgentineEwalletDocumentForceShow } from './use-argentine-ewallet-document-force-show';
import {
  type UserInputValidationError,
  serverErrorMessage,
} from '@tectonics/prs/validation';
import { useIncomingPayment } from '@tectonics/prs/incoming-payment';

import { ErrorLabel } from '../../../../molecules/error-label';
import { FieldCustomerDocument } from '../field-customer-document';
import { FindYourDocumentLink } from './find-your-document-link';

export function FieldArgentineDocument(): Node {
  const [documentType] = useUserInputValue<ArgentineDocumentType>(
    'argentineDocumentType'
  );
  const incomingPayment = useIncomingPayment();
  const forceShow = useArgentineEwalletDocumentForceShow();

  const mask = useMemo(() => {
    return documentType === 'DNI' ? DNI_MASK : OTHER_MASK;
  }, [documentType]);

  const formatRaw = useCallback(
    (rawValue: string) => {
      return formatValueWithMask(rawValue, mask);
    },
    [mask]
  );

  const parseFormatted = useCallback(
    (formattedValue: ?string) => {
      return formattedValue
        ? normalizeArgentineDocument(documentType, formattedValue)
        : '';
    },
    [documentType]
  );

  const additionalContent = useMemo(() => {
    return <FindYourDocumentLink incomingPayment={incomingPayment} />;
  }, [incomingPayment]);

  return (
    <div style={{ position: 'relative' }}>
      <FieldCustomerDocument
        inputData={{
          inputFieldName: 'argentineDocument',
          label: 'Número de Documento',
          specDocumentName: 'field_argentine_document',
        }}
        mask={mask}
        normalizer={parseFormatted}
        formatRaw={formatRaw}
        validationErrorRenderer={ValidationErrorMessage}
        additionalContent={additionalContent}
        forceShow={forceShow}
      />
    </div>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({
  validationError,
}: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>El documento es requerido</ErrorLabel>;
    case 'invalid-dni-pattern':
    case 'invalid-argentine-document-pattern':
      return <ErrorLabel>El documento informado es inválido</ErrorLabel>;

    case 'server-error':
      return (
        <ErrorLabel>{serverErrorMessage(validationError.payload)}</ErrorLabel>
      );

    default:
      return <ErrorLabel>El documento informado es inválido</ErrorLabel>;
  }
}

const DNI_MASK = [
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
];

const OTHER_MASK = [
  /[0-9]/,
  /[0-9]/,
  '-',
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '-',
  /[0-9]/,
];
